import { z } from "zod";

import type {
  InferenceEngine,
  InferenceEngineConfig,
} from "../lib/models.engine-config.types";
import {
  GpuDetectedSchema,
  GpuSlugEnum,
  GpuTypeSchema,
} from "../lib/gpu.models";
import { InferenceModelSchema, INSTANCE_RUNTIME } from "../lib/models.model";
import {
  authedServiceRequest,
  serviceRequest,
  serviceResponse,
} from "./BaseService";

export const InstanceStatusSchema = z.enum([
  "Initializing",
  "Running",
  "Paused",
  "Expired",
]);
export type InstanceStatusEnum = z.infer<typeof InstanceStatusSchema>;

export const InferenceEngineStatusSchema = z.enum([
  "initializing",
  "running",
  "down",
]);

export type InferenceEngineStatusEnum = z.infer<
  typeof InferenceEngineStatusSchema
>;

export const InstanceLocationSchema = z.object({
  country: z.string().nullish(),
  city: z.string().nullish(),
  continent: z.string().nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
  postalCode: z.string().nullish(),
  metroCode: z.string().nullish(),
  region: z.string().nullish(),
  regionCode: z.string().nullish(),
  timezone: z.string().nullish(),
});

export type InstanceLocation = z.infer<typeof InstanceLocationSchema>;

export const InstanceInfoSchema = z.object({
  ipAddress: z.string(),
  location: InstanceLocationSchema.nullish(),
  arch: z.string().nullable(),
  platform: z.string().nullable(),
  type: z.string().nullable(),
  totalMemoryBytes: z.number().nullable(),
  totalSwapBytes: z.number().nullable(),
  systemName: z.string(),
  kernelVersion: z.string().nullable(),
  osVersion: z.string().nullable(),
  hostName: z.string().nullable(),
  cpus: z.number().nullable(),
  version: z.string().nullish(),
  isSystemd: z.boolean().nullable(),
  runtime: z.enum(INSTANCE_RUNTIME).nullish(),
});

export const InstanceLocationInfoSchema = z.object({
  instanceid: z.string(),
  location: InstanceInfoSchema.shape.location.optional(),
  device: z.string().nullable(),
});

export type InstanceLocationInfo = z.infer<typeof InstanceLocationInfoSchema>;

// This is a rough representation of InferenceEngineConfig but its too complicated to duplicate twice here
const InferenceEngineConfigSchema = z.object({
  flags: z.record(z.string(), z.string()),
  environmentVariables: z.record(z.string(), z.string()),
  parallelism: z.number(),
  modelInitializationTimeoutMs: z.number(),
  engine: z.enum(["vllm", "sglang", "trt", "mocked"]),
  model: InferenceModelSchema,
  configFiles: z
    .array(
      z.object({
        name: z.string(),
        config: z.record(z.string(), z.unknown()),
        type: z.literal("yaml"),
      }),
    )
    .optional(),
});

export const INSTANCE_LANES = ["fast", "slow"] as const;

export const InstanceLaneSchema = z.enum(INSTANCE_LANES);
export type InstanceLane = z.infer<typeof InstanceLaneSchema>;

export const InstancePoolAssignmentSchema = z.object({
  workerId: z.string().nullable(),
  instanceId: z.string().nullable(),
  /**
   * @deprecated use inferenceEngineConfig.model instead
   */
  model: InferenceModelSchema,
  lane: InstanceLaneSchema,
  // TODO: Set to not null once we've rolled out to all users
  inferenceEngineConfig: InferenceEngineConfigSchema.nullable(),
});

export const InstanceSchema = z.object({
  _id: z.string(),
  userId: z.string(),
  teamId: z.string(),
  workerId: z.string(),
  name: z.string(),
  status: InstanceStatusSchema,
  gpuHash: z.string(),
  isRunningInDocker: z.boolean(),
  dockerImage: z.string().nullable(),
  dockerImageTag: z.string().nullable(),
  // TODO: Once this is fully rolled out, these should be required
  cliVersion: z.string().nullable(),
  cliVersionHash: z.string().nullable(),
  vllmVersion: z.string().nullable(),
  sglangVersion: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  expiredAt: z.date().nullable(),
});

export const InstanceGpuSchema = z.object({
  _id: z.string(),
  instanceId: z.string(),
  gpuId: z.string(),
  modelName: z.string(),
  vramBytes: z.number(),
  driverVersion: z.string(),
  type: GpuTypeSchema,
  slug: GpuSlugEnum.nullable(),
});

export const InstanceWithGpusSchema = InstanceSchema.merge(
  z.object({
    gpus: z.array(InstanceGpuSchema),
  }),
);

export const InstanceWithInfoAndGpusSchema = InstanceSchema.merge(
  z.object({
    info: InstanceInfoSchema.nullable(),
    gpus: z.array(InstanceGpuSchema),
  }),
);

export const InstanceWithPoolAssignmentSchema = InstanceSchema.merge(
  z.object({
    /**
     * @deprecated use poolAssignment instead
     */
    poolAssignments: z.array(InstancePoolAssignmentSchema),
    poolAssignment: InstancePoolAssignmentSchema.nullable(),
  }),
);

export const InstanceWithInfoAndAssignmentSchema = InstanceSchema.merge(
  z.object({
    info: InstanceInfoSchema,
    /**
     * @deprecated use poolAssignment instead
     */
    poolAssignments: z.array(InstancePoolAssignmentSchema),
    poolAssignment: InstancePoolAssignmentSchema.nullable(),
    gpus: z.array(InstanceGpuSchema),
  }),
);

export type Instance = z.infer<typeof InstanceSchema>;
export type InstanceGpu = z.infer<typeof InstanceGpuSchema>;

export type InstanceWithInfoAndGpus = z.infer<
  typeof InstanceWithInfoAndGpusSchema
>;
export type InstanceWithPoolAssignment = z.infer<
  typeof InstanceWithPoolAssignmentSchema
>;
export type InstanceWithInfoAndAssignment = z.infer<
  typeof InstanceWithInfoAndAssignmentSchema
>;
export type InstanceWithGpus = z.infer<typeof InstanceWithGpusSchema>;

export type InstanceInfo = z.infer<typeof InstanceInfoSchema>;
export type InstanceStatus = z.infer<typeof InstanceStatusSchema>;
export type InstancePoolAssignment = z.infer<
  typeof InstancePoolAssignmentSchema
>;

// Two things going on here:
// 1. were omitting poolAssignments, this is a deprecated field
// 2. we're adding a stronger type for inferenceEngineConfig to poolAssignment,
// the db schema only types the flags as Record<string, string> but our type system requires stronger types
export type CreateInstanceAttributes = Omit<
  InstanceWithInfoAndAssignment,
  "poolAssignments" | "poolAssignment"
> & {
  poolAssignment: Omit<InstancePoolAssignment, "inferenceEngineConfig"> & {
    inferenceEngineConfig: InferenceEngineConfig<InferenceEngine>;
  };
};

/** ******************************************************************************
 *  Count Instances
 ******************************************************************************* */

export const countInstancesParams = z.undefined();

export const countInstancesRequest = serviceRequest.merge(
  z.object({
    params: countInstancesParams,
  }),
);

export const countInstancesResponse = serviceResponse.merge(
  z.object({
    count: z.number().optional(),
  }),
);

export type CountInstancesParams = z.infer<typeof countInstancesParams>;
export type CountInstancesRequest = z.infer<typeof countInstancesRequest>;
export type CountInstancesResponse = z.infer<typeof countInstancesResponse>;

export const instanceHardwareRow = z.object({
  gpu: z.string(),
  count: z.number(),
  percentage: z.number(),
  totalVRAM: z.string().nullable(),
  type: GpuTypeSchema,
});

export const instanceHardwareParams = z.undefined();

export const instanceHardwareRequest = serviceRequest.merge(
  z.object({
    params: instanceHardwareParams,
  }),
);

export const instanceHardwareResponse = serviceResponse.merge(
  z.object({
    gpus: z.array(instanceHardwareRow).optional(),
    totalCount: z.number().optional(),
    totalVRAM: z.string().optional(),
  }),
);

export type InstanceHardwareRow = z.infer<typeof instanceHardwareRow>;
export type InstanceHardwareParams = z.infer<typeof instanceHardwareParams>;
export type InstanceHardwareRequest = z.infer<typeof instanceHardwareRequest>;
export type InstanceHardwareResponse = z.infer<typeof instanceHardwareResponse>;

/** ******************************************************************************
 *  Initialize Instance
 ******************************************************************************* */

export const initializeInstanceParams = z.object({
  name: z.string().nullable(),
  workerId: z.string(),
  registrationCode: z.string(),
  instanceId: z.string().nullish(),
  info: InstanceInfoSchema,
  gpus: z.array(GpuDetectedSchema),
  // TODO: we could calculate this server side now, and then ensure
  // the one the client sends matches the one the server calculates.
  // Would be harder to game that way
  gpuHash: z.string(),
  // TODO: Make these required once theyre fully rolled out

  isRunningInDocker: z.boolean(),
  dockerImage: z.string().optional().nullable(),
  dockerImageTag: z.string().optional().nullable(),
  vllmVersion: z.string().optional().nullable(),
  sglangVersion: z.string().optional().nullable(),
  cliVersion: z.string().optional().nullable(),
  cliVersionHash: z.string().optional().nullable(),
});

export const initializeInstanceRequest = serviceRequest.merge(
  z.object({
    params: initializeInstanceParams,
  }),
);

export const initializeInstanceResponse = serviceResponse.merge(
  z.object({
    teamId: z.string().optional(),
    instanceId: z.string().optional(),
    poolAssignment: InstancePoolAssignmentSchema.optional(),
    // JWT for initial NATS connection
    token: z.string().optional(),
  }),
);

export type InitializeInstanceParams = z.infer<typeof initializeInstanceParams>;
export type InitializeInstanceRequest = z.infer<
  typeof initializeInstanceRequest
>;
export type InitializeInstanceResponse = z.infer<
  typeof initializeInstanceResponse
>;

/** ******************************************************************************
 *  Instance Heartbeat
 ******************************************************************************* */

/** ******************************************************************************
 *  Restart Instance
 ******************************************************************************* */

export const restartInstancesParams = z.object({
  instanceIds: z.array(z.string()),
});

export const restartInstancesRequest = authedServiceRequest.merge(
  z.object({
    params: restartInstancesParams,
  }),
);

export const restartInstancesResponse = serviceResponse.merge(
  z.object({
    success: z.boolean(),
  }),
);

export type RestartInstancesParams = z.infer<typeof restartInstancesParams>;
export type RestartInstancesRequest = z.infer<typeof restartInstancesRequest>;
export type RestartInstancesResponse = z.infer<typeof restartInstancesResponse>;

/** ******************************************************************************
 *  Set Instances Engine Config
 ******************************************************************************* */

export const setInstancesEngineConfigParams = z.object({
  instanceIds: z.array(z.string()),
  model: InferenceModelSchema,
  parallelism: z.number(),
});

export const setInstancesEngineConfigRequest = authedServiceRequest.merge(
  z.object({
    params: setInstancesEngineConfigParams,
  }),
);

export const setInstancesEngineConfigResponse = serviceResponse.merge(
  z.object({
    success: z.boolean(),
  }),
);

export type SetInstancesEngineConfigParams = z.infer<
  typeof setInstancesEngineConfigParams
>;
export type SetInstancesEngineConfigRequest = z.infer<
  typeof setInstancesEngineConfigRequest
>;
export type SetInstancesEngineConfigResponse = z.infer<
  typeof setInstancesEngineConfigResponse
>;

/** ******************************************************************************
 *  Get Instance By ID (with info, gpus, assignment)
 ******************************************************************************* */

export const getInstanceByIdParams = z.object({
  instanceId: z.string(),
});

export const getInstanceByIdRequest = authedServiceRequest.merge(
  z.object({
    params: getInstanceByIdParams,
  }),
);

export const getInstanceByIdResponse = serviceResponse.merge(
  z.object({
    instance: InstanceWithInfoAndAssignmentSchema.nullable().optional(),
  }),
);

export type GetInstanceByIdParams = z.infer<typeof getInstanceByIdParams>;
export type GetInstanceByIdRequest = z.infer<typeof getInstanceByIdRequest>;
export type GetInstanceByIdResponse = z.infer<typeof getInstanceByIdResponse>;

/** ******************************************************************************
 *  Super List Instances
 ******************************************************************************* */

export const superListInstancesParams = z.object({
  statuses: z.array(InstanceStatusSchema),
});

export const superListInstancesRequest = authedServiceRequest.merge(
  z.object({
    params: superListInstancesParams,
  }),
);

export const superListInstancesResponse = serviceResponse.merge(
  z.object({
    instances: z.array(InstanceWithInfoAndAssignmentSchema).optional(),
    total: z.number().optional(),
  }),
);

export type SuperListInstancesParams = z.infer<typeof superListInstancesParams>;
export type SuperListInstancesRequest = z.infer<
  typeof superListInstancesRequest
>;
export type SuperListInstancesResponse = z.infer<
  typeof superListInstancesResponse
>;
